import { useCallback, useEffect, useRef, useState } from 'react';
import { runHoldTransition } from '../../app/transitions';
import { transitionControlFor } from '../../domain/chrome';
import { createConfirmingHold } from '../../ui/capture/confirmingHold';
import type { ConfirmingHold } from '../../ui/capture/confirmingHold';
import { Icon } from '../Icon';
import type { MatchRow } from '../../types';

/**
 * B-8 — the **guarded whistle** in the takeover's top band (design spec §2.4; scenario 7).
 *
 * One control, four transitions, and everything that decides or times anything is reused, not
 * rebuilt (requirements §5.2 — three of the four pieces are engine or engine-adjacent):
 *
 * - **which** control this state shows — `domain/chrome.transitionControlFor` (the map derived
 *   from §2.2's table; exactly one hold per state, proven at module load);
 * - **the hold mechanics** — A's `confirmingHold.ts`, imported as-is (baseline §7 names it a
 *   reusable primitive with no A-design coupling). Its two load-bearing rules are the engine
 *   contract this unit must honor: the anchor is read **once, at `pointerdown`**, and completion
 *   **fires from the timer while the finger is down, never from pointerup** — a release-checked
 *   elapsed time would hand the machine an anchor `n×` the hold old, outside
 *   `[0, TRANSITION_HOLD_MS + HOLD_SLOP_MS]`, and `machine.transition` never substitutes its own
 *   clock (SES-1);
 * - **the dispatch** — `app/transitions.runHoldTransition(matchId, event, anchorTs)`, the
 *   facade whose `anchorTs` parameter has deliberately no default.
 *
 * What is B's here is the rendering: the pill, the fill, the pressed feedback within one frame
 * of `pointerdown` (skill §2 — all acknowledgment is visual on iOS web), and where a rejection
 * lands (overlaid, never in-flow — the chrome must not move under a thumb mid-match).
 *
 * Deliberately absent, as in A's adapter and for the same reasons: no `onClick` (a control whose
 * contract is a sustained press has no meaning for a synthesized click); no movement slop (the
 * control carries `touch-action: none`, and a thumb drifting 12 px through a deliberate 700 ms
 * press is still deliberate — `pointercancel`, the browser taking the gesture, does cancel);
 * no Abandon (§2.4: Abandon lives behind the overflow with a confirm, anchor source `now`, and
 * `transitionControlFor` structurally cannot offer it a hold slot).
 */
export interface TransitionControlBProps {
  readonly match: MatchRow;
  /** Called after the transition commits, so the host re-reads the row it renders. */
  readonly onTransitioned: () => void;
}

export function TransitionControlB({ match, onTransitioned }: TransitionControlBProps) {
  const control = transitionControlFor(match.state);
  const [holding, setHolding] = useState(false);
  const [rejection, setRejection] = useState<string | null>(null);
  const busy = useRef(false);
  const hold = useRef<ConfirmingHold | null>(null);

  /**
   * The dispatch, with the anchor **carried** rather than sampled: `anchorTs` arrives from the
   * hold controller as the timestamp it read at `pointerdown`. Nothing here reads a clock — the
   * first clock reading after press-start is `machine.transition`'s own `now`, inside the
   * transaction, which validates the anchor rather than replacing it.
   */
  const complete = useCallback(
    (anchorTs: number): void => {
      if (control === null || busy.current) return;
      busy.current = true;
      setRejection(null);
      void runHoldTransition(match.id, control.event, anchorTs).then(
        (result) => {
          busy.current = false;
          // SES-1 — rejection is a returned value, surfaced non-blockingly; nothing else changes.
          if (!result.ok) {
            setRejection(result.message);
            return;
          }
          onTransitioned();
        },
        (cause: unknown) => {
          busy.current = false;
          setRejection(cause instanceof Error ? cause.message : String(cause));
        },
      );
    },
    [control, match.id, onTransitioned],
  );

  const completeRef = useRef(complete);
  completeRef.current = complete;

  useEffect(() => {
    const controller = createConfirmingHold({
      holdMs: control?.holdMs ?? 0,
      now: () => Date.now(),
      setTimer: (fn, ms) => window.setTimeout(fn, ms),
      clearTimer: (id) => {
        window.clearTimeout(id);
      },
      onComplete: (anchorTs) => {
        completeRef.current(anchorTs);
      },
      onHoldingChange: setHolding,
    });
    hold.current = controller;
    return () => {
      controller.dispose();
      hold.current = null;
    };
    // Re-created when the legal transition changes, so a hold armed against the previous state
    // can never complete into the new one — the state moved out from under the finger.
  }, [control?.event, control?.holdMs]);

  /**
   * §2.4 — states with no hold (`fullTime` for its one render before the hand-off) render
   * nothing; the strip beside this slot carries the band's height, so nothing below moves.
   */
  if (control === null) return null;

  return (
    <>
      <button
        type="button"
        className="capb-hold"
        data-testid="capb-hold"
        data-event={control.event}
        data-holding={holding ? 'true' : undefined}
        aria-label={control.label}
        onPointerDown={(event) => {
          // Explicit capture so a finger that drifts off the pill still delivers its pointerup
          // here — otherwise a slight slide would leave the fill running with nothing to cancel
          // it. Refusal (pointer no longer active) falls back to implicit capture, the guard A's
          // adapter carries from a browser-pass finding.
          try {
            event.currentTarget.setPointerCapture(event.pointerId);
          } catch {
            hold.current?.press();
            return;
          }
          hold.current?.press();
        }}
        onPointerUp={() => hold.current?.cancel()}
        onPointerCancel={() => hold.current?.cancel()}
        onPointerLeave={() => hold.current?.cancel()}
      >
        {/* The fill IS the affordance — luminance + coverage, transform/opacity only, no layout.
            It lives inside the pill, so it can neither consume the strip nor reflow the band.
            Its duration is set HERE, from the same holdMs the completion timer runs on, so the
            visual and the commit cannot disagree (and the token-tier guard stays honest: no
            stylesheet copy of TRANSITION_HOLD_MS exists to drift). */}
        <span
          className="capb-hold__fill"
          aria-hidden="true"
          style={{ animationDuration: `${String(control.holdMs)}ms` }}
        />
        <span className="capb-hold__face">
          <Icon name="whistle" />
          <span className="capb-hold__label" data-testid="capb-hold-label">
            {control.label}
          </span>
        </span>
      </button>

      {/* Overlaid beneath the band (position: absolute in capture.css), never in-flow: a
          rejection is rare, and the one thing it must not do is move the chrome mid-match. */}
      {rejection !== null && (
        <p className="capb-hold__rejection" role="status" data-testid="capb-hold-rejection">
          {rejection}
        </p>
      )}
    </>
  );
}
